import { useEffect, useMemo, useState } from "react";
import { Check, X, Image as ImageIcon, Loader2, ShieldCheck, AlertTriangle, ShieldAlert, Filter, Search, Inbox, Lightbulb, Lock, AlertCircle, Sparkles } from "lucide-react";
import api, { fmtINR, shortDate, formatDetail } from "@/lib/api";
import Modal from "@/components/Modal";
import PinChannelButton from "@/components/PinChannelButton";
import { SummaryCards } from "@/pages/admin/AdminTransactions";
import { useAuth } from "@/lib/auth";
import { buildWebsiteColorMap } from "@/lib/websiteColors";
import AssignUtrModal from "@/pages/admin/AssignUtrModal";
import BulkAssignUtrModal from "@/pages/admin/BulkAssignUtrModal";

const CATEGORY_LABELS = {
    0: "FTD (1st deposit)",
    1: "2nd deposit",
    2: "3rd deposit",
    3: "4th deposit",
    4: "5th + repeat",
};

function HintBadge({ hint, onAccept, busy, canAccept }) {
    if (!hint) return <span className="pill pill-muted">—</span>;
    if (hint.level === "good")
        return (
            <span className="pill bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 inline-flex items-center gap-1" data-testid="hint-good">
                <ShieldCheck className="w-3 h-3" /> {hint.label}
            </span>
        );
    if (hint.level === "danger")
        return (
            <span className="pill bg-rose-50 text-rose-700 ring-1 ring-rose-200 inline-flex items-center gap-1" data-testid="hint-danger">
                <ShieldAlert className="w-3 h-3" /> {hint.label}
            </span>
        );
    // Neutral: near-match gets an inline action button (admin-only); plain manual review stays as a pill.
    if (hint.action === "whitelist_and_approve" && hint.sms_id && onAccept) {
        return (
            <button data-testid="hint-near-match"
                    onClick={() => onAccept(hint.sms_id)}
                    disabled={busy || canAccept === false}
                    title={canAccept === false
                        ? "Only the partner admin can whitelist new senders"
                        : `Whitelist '${hint.suggested_sender_id}' and auto-approve this txn`}
                    className="pill bg-amber-50 text-amber-800 ring-1 ring-amber-200 hover:bg-amber-100 inline-flex items-center gap-1 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" />
                    : (canAccept === false ? <Lock className="w-3 h-3" /> : <Lightbulb className="w-3 h-3" />)}
                {hint.label} · Whitelist & approve
            </button>
        );
    }
    return (
        <span className="pill bg-slate-100 text-slate-600 inline-flex items-center gap-1" data-testid="hint-neutral">
            <AlertTriangle className="w-3 h-3" /> {hint.label}
        </span>
    );
}

export default function AdminApprovals({ scope = "admin" }) {
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [previewTxn, setPreviewTxn] = useState(null);
    const [assignTxn, setAssignTxn] = useState(null);  // open Assign-UTR modal
    const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [firstLoaded, setFirstLoaded] = useState(false);
    const [rowBusy, setRowBusy] = useState(null);
    const [filterCategory, setFilterCategory] = useState("");
    const [filterChannel, setFilterChannel] = useState("");
    const [linkFilter, setLinkFilter] = useState("");
    const [q, setQ] = useState("");
    const [sort, setSort] = useState("oldest");
    const [error, setError] = useState("");
    const [info, setInfo] = useState("");
    const [channelOpts, setChannelOpts] = useState(() => new Map());
    // Partner-only: SMS near-match suggestions — rows where amount+UTR match
    // a pending txn but the sender ID wasn't whitelisted. Surfaced here so
    // the partner catches bottlenecks from unknown bank sender variants.
    const [suggestions, setSuggestions] = useState([]);
    const [suggBusy, setSuggBusy] = useState(null);
    const [confirmSugg, setConfirmSugg] = useState(null);  // suggestion-pending-confirmation
    const { user: currentUser } = useAuth();
    const isPartnerAdmin = currentUser?.role === "partner_admin";

    const baseUrl = scope === "admin" ? "/admin/transactions" : "/partner/transactions";
    const summaryUrl = scope === "admin" ? "/admin/transactions/summary-by-link" : "/partner/transactions/summary-by-link";

    const load = async () => {
        setBusy(true);
        // Approvals page surfaces both "submitted" (UTR/screenshot uploaded,
        // awaiting verification) AND "pending" (customer never completed) so
        // partners can manually mark dropped attempts as Failed without having
        // to switch pages — pending txns are otherwise invisible to ops.
        const params = new URLSearchParams({ status: "pending,submitted" });
        if (sort === "oldest") params.set("sort", "oldest");
        if (filterCategory !== "") params.set("category", filterCategory);
        if (filterChannel) params.set("channel_id", filterChannel);
        if (linkFilter) params.set("link_code", linkFilter);
        if (q) params.set("q", q);
        try {
            const r = await api.get(`${baseUrl}?${params}`);
            setItems(r.data.items || []);
        } finally {
            setBusy(false);
            setFirstLoaded(true);
        }
    };
    const loadSummary = async () => {
        try {
            const r = await api.get(`${summaryUrl}?status=pending,submitted`);
            setSummary(r.data.items || []);
        } catch { /* non-fatal */ }
    };
    const loadSuggestions = async () => {
        if (scope !== "partner") return;
        try {
            const r = await api.get("/sms/suggestions");
            setSuggestions(r.data.items || []);
        } catch { /* non-fatal */ }
    };
    const backfillSuggestions = async () => {
        setSuggBusy("__backfill__"); setError(""); setInfo("");
        try {
            const r = await api.post("/sms/suggestions/backfill");
            await loadSuggestions();
            // Only surface a banner when something actionable happened.
            // Silent on "tagged 0" — agents see existing amber pills inline,
            // a noisy "0 new" red banner is misleading.
            if (r.data.tagged > 0) {
                setInfo(`Tagged ${r.data.tagged} new near-match${r.data.tagged === 1 ? "" : "es"} from ${r.data.scanned} recent SMS — see amber pills below.`);
                setTimeout(() => setInfo(""), 6000);
            }
        } catch (e) {
            setError(formatDetail(e?.response?.data?.detail) || "Backfill failed");
        } finally { setSuggBusy(null); }
    };
    const acceptSuggestion = async (smsId) => {
        // Stage the suggestion for confirmation rather than firing immediately
        // — whitelisting a sender is privileged and not safe to auto-execute.
        let s = suggestions.find((x) => x.id === smsId);
        if (!s) {
            // The hint button on a row can fire for an SMS that's not in
            // the standalone suggestions list yet. Synthesize a minimal
            // suggestion from the current row context.
            try {
                const r = await api.get("/sms/suggestions");
                s = (r.data.items || []).find((x) => x.id === smsId);
                setSuggestions(r.data.items || []);
            } catch { /* ignore */ }
        }
        if (s) setConfirmSugg(s);
    };
    const confirmAcceptSuggestion = async () => {
        if (!confirmSugg) return;
        const smsId = confirmSugg.id;
        setSuggBusy(smsId);
        try {
            await api.post(`/sms/suggestions/${smsId}/accept`);
            setConfirmSugg(null);
            await Promise.all([loadSuggestions(), load(), loadSummary()]);
        } catch (e) {
            setError(formatDetail(e?.response?.data?.detail) || "Could not accept suggestion");
        } finally { setSuggBusy(null); }
    };
    const dismissSuggestion = async (smsId) => {
        setSuggBusy(smsId);
        try {
            await api.post(`/sms/suggestions/${smsId}/dismiss`);
            await loadSuggestions();
        } finally { setSuggBusy(null); }
    };
    // Persist the full set of channels + payment-links from dedicated
    // endpoints so the filter dropdowns stay populated even when the
    // current results list is empty (no submitted approvals yet, or
    // user just filtered everything out).
    const [channelsList, setChannelsList] = useState([]);
    const [linksList, setLinksList] = useState([]);
    const loadStaticOptions = async () => {
        try {
            if (scope === "partner") {
                const [u, b, l] = await Promise.all([
                    api.get("/partner/upi-channels"),
                    api.get("/partner/bank-accounts"),
                    api.get("/partner/payment-links"),
                ]);
                const all = [
                    ...(u.data.items || []).map((c) => ({ id: c.channel_id, label: `UPI · ${c.label || c.upi_id || c.channel_id}` })),
                    ...(b.data.items || []).map((c) => ({ id: c.channel_id, label: `BANK · ${c.label || c.bank_name || c.channel_id}` })),
                ];
                setChannelsList(all);
                setLinksList(l.data.items || []);
            } else {
                // Admin scope: aggregate via the summary endpoint (good
                // enough — it returns all link_codes that have ever had a
                // transaction). Channels fall back to the accumulated
                // channelOpts map (collected as items load).
                const lAll = await api.get(`${summaryUrl}`);
                setChannelsList([]);
                const seen = new Map();
                for (const it of (lAll.data.items || [])) {
                    if (it.link_code && !seen.has(it.link_code)) {
                        seen.set(it.link_code, { link_code: it.link_code, display_name: it.display_name });
                    }
                }
                setLinksList([...seen.values()]);
            }
        } catch { /* non-fatal — keep the dropdowns empty */ }
    };
    useEffect(() => { load();   }, [scope, filterCategory, filterChannel, linkFilter, sort]);
    useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t);   }, [q]);
    useEffect(() => { loadSummary(); loadStaticOptions(); loadSuggestions();   }, [scope]);

    // Whenever new items arrive, merge any unseen channels into the persistent
    // option list. Map preserves insertion order so the dropdown stays stable.
    useEffect(() => {
        setChannelOpts((prev) => {
            let changed = false;
            const next = new Map(prev);
            items.forEach((t) => {
                const id = t.channel_id;
                if (id && !next.has(id)) {
                    next.set(id, `${(t.channel_snapshot || {}).label || "—"} · ${t.channel_type}`);
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [items]);

    // Merge "ever-seen" channels from the API list with any channel found
    // in the current items (fallback for legacy txns whose channel_id may
    // not appear in the static list).
    const channelOptions = useMemo(() => {
        const map = new Map();
        for (const c of channelsList) map.set(c.id, c.label);
        for (const [id, label] of channelOpts.entries()) {
            if (!map.has(id)) map.set(id, label);
        }
        return [...map.entries()];
    }, [channelsList, channelOpts]);

    const sortedItems = useMemo(() => {
        const arr = [...items];
        switch (sort) {
            case "amount_desc": arr.sort((a, b) => (b.amount || 0) - (a.amount || 0)); break;
            case "amount_asc":  arr.sort((a, b) => (a.amount || 0) - (b.amount || 0)); break;
            case "username":    arr.sort((a, b) => (a.customer_username || "").localeCompare(b.customer_username || "")); break;
            // oldest/newest already sorted by server
        }
        return arr;
    }, [items, sort]);

    // Per-website colour map — same lookup used on Successful Txns so
    // agents see the same colour for the same site across both pages.
    // Recomputed when items change. Returns NEUTRAL for untagged rows.
    const colorOf = useMemo(() => buildWebsiteColorMap(sortedItems), [sortedItems]);

    const smsVerifiedIds = useMemo(
        () => sortedItems.filter((t) => t?.verification_hint?.kind === "sms").map((t) => t.txn_id),
        [sortedItems],
    );
    // Screenshot-only rows that need a UTR assigned — feeds the Bulk
    // Assign walkthrough. We exclude rows already showing a UTR
    // (defensive — UI shouldn't render the badge in that case but if
    // the row was just optimistically updated, this prevents a stale
    // entry from reappearing in the bulk queue).
    const awaitingUtrRows = useMemo(
        () => sortedItems.filter((t) => !t.utr && t.screenshot),
        [sortedItems],
    );

    const toggle = (id) => {
        const s = new Set(selected);
        s.has(id) ? s.delete(id) : s.add(id);
        setSelected(s);
    };
    const toggleAll = () => {
        if (selected.size === sortedItems.length) setSelected(new Set());
        else setSelected(new Set(sortedItems.map((t) => t.txn_id)));
    };

    const act = async (action, ids, { rowId = null } = {}) => {
        if (ids.length === 0) return;
        setError("");
        if (rowId) setRowBusy(`${action}:${rowId}`); else setBusy(true);
        try {
            const url = scope === "admin" ? "/admin/approvals" : "/partner/approvals";
            const reason = action === "reject" ? (window.prompt("Reason for rejection?", "") || "Rejected") : undefined;
            const res = await api.post(url, { txn_ids: ids, action, reason });
            const failed = (res.data.results || []).filter((r) => !r.ok);
            if (failed.length) setError(`${failed.length} could not be processed: ${failed.map((f) => f.reason).join(", ")}`);
            setSelected((prev) => {
                const ns = new Set(prev);
                ids.forEach((i) => ns.delete(i));
                return ns;
            });
            await load();
            await loadSummary();
        } catch (e) {
            setError(formatDetail(e?.response?.data?.detail) || "Action failed");
        } finally { setBusy(false); setRowBusy(null); }
    };

    return (
        <div className="space-y-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Approvals</h1>
                    <p className="text-sm text-slate-500 mt-1">Oldest pending first. Use suggestions to spot SMS-verified payments quickly.</p>
                </div>
                <div className="flex gap-2">
                    {scope === "partner" && (
                        <button data-testid="backfill-suggestions" disabled={suggBusy === "__backfill__"}
                            onClick={backfillSuggestions}
                            title="Scan recent SMS logs and tag any where UTR + amount already match a pending txn but the sender wasn't whitelisted. Run once after enabling this feature."
                            className="btn-secondary px-3 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-40">
                            {suggBusy === "__backfill__" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lightbulb className="w-4 h-4" />}
                            Scan existing SMS
                        </button>
                    )}
                    <button data-testid="approve-sms-verified" disabled={!smsVerifiedIds.length || busy}
                        onClick={() => act("approve", smsVerifiedIds)}
                        title="Approve every row whose SMS log already verified the UTR + amount"
                        className="btn-secondary border-emerald-200 text-emerald-700 hover:bg-emerald-50 px-4 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-40">
                        <ShieldCheck className="w-4 h-4" /> Approve all SMS-verified ({smsVerifiedIds.length})
                    </button>
                    {/* Bulk-assign walkthrough — only shown when there's at least one
                        screenshot-only row in the current filtered view. Saves the
                        merchant from clicking each row individually. */}
                    {awaitingUtrRows.length > 0 && (
                        <button data-testid="bulk-assign-utr"
                            onClick={() => setBulkAssignOpen(true)}
                            title="Walk through every screenshot-only row in one modal. Re-runs OCR per row and saves on demand."
                            className="btn-secondary border-amber-300 text-amber-800 hover:bg-amber-50 px-4 py-2 rounded-md text-sm flex items-center gap-2">
                            <Sparkles className="w-4 h-4" /> Bulk Assign UTRs ({awaitingUtrRows.length})
                        </button>
                    )}
                    <button data-testid="bulk-reject" disabled={!selected.size || busy} onClick={() => act("reject", [...selected])} className="btn-danger px-4 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-40">
                        <X className="w-4 h-4" /> Reject ({selected.size})
                    </button>
                    <button data-testid="bulk-approve" disabled={!selected.size || busy} onClick={() => act("approve", [...selected])} className="btn-emerald px-4 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-40">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Approve ({selected.size})
                    </button>
                </div>
            </div>

            {summary.length > 0 && (
                <SummaryCards
                    summary={summary}
                    activeLink={linkFilter}
                    onPick={(code) => setLinkFilter(linkFilter === code ? "" : code)}
                    accent="amber"
                    label="Pending"
                />
            )}

            <div className="card p-3 sm:p-4 flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[220px]">
                    <label className="label flex items-center gap-1.5"><Search className="w-3 h-3" /> Universal search</label>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input data-testid="approvals-search" value={q} onChange={(e) => setQ(e.target.value)}
                                className="input pl-9" placeholder="Username, UTR, txn id…" />
                    </div>
                </div>
                <div>
                    <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">Deposit count</label>
                    <select data-testid="filter-category" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="input w-44">
                        <option value="">All</option>
                        {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">Channel / Bank</label>
                    <select data-testid="filter-channel" value={filterChannel} onChange={(e) => setFilterChannel(e.target.value)} className="input w-56">
                        <option value="">All</option>
                        {channelOptions.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">Website / Link</label>
                    <select data-testid="filter-link" value={linkFilter} onChange={(e) => setLinkFilter(e.target.value)} className="input w-48">
                        <option value="">All websites</option>
                        {linksList.map((l) => <option key={l.link_code} value={l.link_code}>{l.display_name || l.link_code}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">Sort by</label>
                    <select data-testid="approvals-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="input w-44">
                        <option value="oldest">Oldest first</option>
                        <option value="newest">Newest first</option>
                        <option value="amount_desc">Highest amount</option>
                        <option value="amount_asc">Lowest amount</option>
                        <option value="username">Username A-Z</option>
                    </select>
                </div>
                {(linkFilter || filterCategory !== "" || filterChannel || q) && (
                    <button data-testid="approvals-clear-filters"
                            onClick={() => { setLinkFilter(""); setFilterCategory(""); setFilterChannel(""); setQ(""); }}
                            className="text-xs text-blue-700 hover:underline px-2 py-2 flex items-center gap-1">
                        <Filter className="w-3 h-3" /> Clear all
                    </button>
                )}
            </div>

            {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>}
            {info && <div data-testid="info-banner" className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">{info}</div>}

            {/* Per-website colour legend — shown only when ≥2 tagged
                websites are present in the current view. Same treatment
                as Successful Txns so the colour grammar stays consistent
                across pages. */}
            {(() => {
                const names = Array.from(new Set(sortedItems.map((t) => t.website_name).filter(Boolean))).sort((a, b) => a.localeCompare(b));
                if (names.length < 2) return null;
                return (
                    <div data-testid="approvals-color-legend" className="flex flex-wrap items-center gap-2 px-1 text-[11px]">
                        <span className="text-slate-400 uppercase tracking-wider font-medium">Legend:</span>
                        {names.map((n) => {
                            const c = colorOf(n);
                            return (
                                <span key={n} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border font-semibold ${c.pill}`}>
                                    <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: c.stripe }} />
                                    {c.initial && (
                                        <span className="font-mono text-[9px] tracking-wider opacity-70">
                                            {c.initial}
                                        </span>
                                    )}
                                    {n}
                                </span>
                            );
                        })}
                    </div>
                );
            })()}

            <div className="card overflow-hidden">
                <div className="min-h-[260px] relative">
                    {busy && firstLoaded && (
                        <div className="absolute top-0 right-0 m-3 z-10 inline-flex items-center gap-1.5 text-[11px] text-slate-500 bg-white/80 backdrop-blur-sm px-2 py-1 rounded-full border border-slate-200">
                            <Loader2 className="w-3 h-3 animate-spin" /> updating
                        </div>
                    )}
                    {!firstLoaded ? (
                        <div className="px-4 py-16 text-center text-slate-400 text-sm">
                            <Loader2 className="w-5 h-5 mx-auto animate-spin opacity-60" />
                            <div className="mt-2">Loading approvals…</div>
                        </div>
                    ) : sortedItems.length === 0 ? (
                        (filterCategory !== "" || filterChannel || linkFilter || q) ? (
                            <div className="px-4 py-12 text-center" data-testid="approvals-empty-filtered">
                                <Inbox className="w-6 h-6 mx-auto opacity-40" />
                                <div className="text-sm text-slate-500 mt-2">No pending approvals match your filters.</div>
                                <button
                                    data-testid="clear-filters"
                                    onClick={() => { setFilterCategory(""); setFilterChannel(""); setLinkFilter(""); setQ(""); }}
                                    className="mt-3 text-xs font-medium text-blue-700 hover:text-blue-800 underline">
                                    Clear filters
                                </button>
                            </div>
                        ) : (
                            <div className="px-4 py-16 text-center text-slate-400 text-sm" data-testid="approvals-empty">
                                <Inbox className="w-6 h-6 mx-auto opacity-40" />
                                <div className="mt-2">No pending approvals.</div>
                            </div>
                        )
                    ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="px-3 py-2.5"><input type="checkbox" data-testid="bulk-toggle" checked={selected.size === sortedItems.length && sortedItems.length > 0} onChange={toggleAll} /></th>
                                <th className="px-4 py-2.5 text-left">Username</th>
                                <th className="px-4 py-2.5 text-left">Website</th>
                                <th className="px-4 py-2.5 text-left">Method · Channel</th>
                                <th className="px-4 py-2.5 text-left">Deposit</th>
                                <th className="px-4 py-2.5 text-left">UTR</th>
                                <th className="px-4 py-2.5 text-right">Amount</th>
                                <th className="px-4 py-2.5 text-left">Submitted</th>
                                <th className="px-4 py-2.5 text-left">Suggestion</th>
                                <th className="px-4 py-2.5 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedItems.map((t) => {
                                const cat = t.category_at_assignment ?? 0;
                                const channelLabel = (t.channel_snapshot || {}).label || "—";
                                // Per-website tint + stripe — mirrors the
                                // Successful Txns treatment so an agent
                                // who's learned the colour-to-website map
                                // on that page can scan this one without
                                // re-orienting.
                                const ws = colorOf(t.website_name);
                                return (
                                    <tr key={t.txn_id} data-testid={`approval-row-${t.txn_id}`}
                                        style={{ backgroundColor: ws.tint }}
                                        className="border-b border-slate-100 last:border-0 hover:brightness-95 transition-all align-middle">
                                        <td className="px-3 py-3 border-l-[6px]" style={{ borderLeftColor: ws.stripe }}>
                                            <input type="checkbox" checked={selected.has(t.txn_id)} onChange={() => toggle(t.txn_id)} />
                                        </td>
                                        <td className="px-4 py-3 font-medium">
                                            {t.customer_username}
                                            {t.late_submission && (
                                                <span data-testid={`late-submission-${t.txn_id}`}
                                                      className="ml-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-200"
                                                      title={`Submitted after the auto-expiry window. Original reason: ${t.prior_reject_reason || "n/a"}`}>
                                                    ⏱ Late
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-xs">
                                            {t.website_name ? (
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-semibold truncate max-w-[160px] ${ws.pill}`} title={t.website_name}>
                                                    {ws.initial && (
                                                        <span className="font-mono text-[9px] tracking-wider opacity-70 border-r pr-1 mr-0.5 border-current/30">
                                                            {ws.initial}
                                                        </span>
                                                    )}
                                                    {t.website_name}
                                                </span>
                                            ) : (
                                                <span className="text-slate-300 text-[11px]">—</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="pill pill-info">{t.channel_type}</span> <span className="text-xs text-slate-600">{channelLabel}</span>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-slate-600">{CATEGORY_LABELS[cat] || `${cat}+`}</td>
                                        <td className="px-4 py-3 font-mono text-xs">
                                            {t.utr || (
                                                <button data-testid={`assign-utr-open-${t.txn_id}`}
                                                        onClick={() => setAssignTxn(t)}
                                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 transition-colors cursor-pointer"
                                                        title="Re-run OCR or type the UTR you see on the screenshot">
                                                    <AlertCircle className="w-3 h-3" /> Assign UTR
                                                </button>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-right font-mono">{fmtINR(t.amount)}</td>
                                        <td className="px-4 py-3 text-xs text-slate-500">{shortDate(t.submitted_at || t.created_at)}</td>
                                        <td className="px-4 py-3"><HintBadge hint={t.verification_hint} onAccept={scope === "partner" ? acceptSuggestion : undefined} busy={suggBusy === t.verification_hint?.sms_id} canAccept={isPartnerAdmin} /></td>
                                        <td className="px-4 py-3">
                                            <div className="flex justify-end gap-1.5">
                                                <PinChannelButton
                                                    customerUsername={t.customer_username}
                                                    customerUserId={t.customer_user_id}
                                                    partnerId={t.partner_id}
                                                    scope={scope}
                                                    label=""
                                                />
                                                {t.screenshot && (
                                                    <button data-testid={`view-screenshot-${t.txn_id}`} onClick={() => setPreviewTxn(t)} className="btn-secondary px-2 py-1.5 rounded-md text-xs flex items-center gap-1" title="View screenshot">
                                                        <ImageIcon className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                                <button data-testid={`row-reject-${t.txn_id}`}
                                                    disabled={rowBusy === `reject:${t.txn_id}`}
                                                    onClick={() => act("reject", [t.txn_id], { rowId: t.txn_id })}
                                                    className="btn-secondary border-rose-200 text-rose-700 hover:bg-rose-50 px-2.5 py-1.5 rounded-md text-xs flex items-center gap-1 disabled:opacity-40" title="Reject">
                                                    {rowBusy === `reject:${t.txn_id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                                                    Reject
                                                </button>
                                                <button data-testid={`row-approve-${t.txn_id}`}
                                                    disabled={rowBusy === `approve:${t.txn_id}`}
                                                    onClick={() => act("approve", [t.txn_id], { rowId: t.txn_id })}
                                                    className="btn-emerald px-2.5 py-1.5 rounded-md text-xs flex items-center gap-1 disabled:opacity-40" title="Approve">
                                                    {rowBusy === `approve:${t.txn_id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                                                    Approve
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    )}
                </div>
            </div>

            {previewTxn && (
                <Modal testId="screenshot-modal" onClose={() => setPreviewTxn(null)} title={`Screenshot · ${previewTxn.customer_username}`} size="lg">
                    <div className="space-y-3">
                        <div className="flex items-center justify-between text-sm">
                            <div>UTR: <span className="font-mono">{previewTxn.utr}</span></div>
                            <div className="font-mono">{fmtINR(previewTxn.amount)}</div>
                        </div>
                        <img src={previewTxn.screenshot} alt="screenshot" className="w-full max-h-[60vh] object-contain rounded-md border border-slate-200" />
                    </div>
                </Modal>
            )}

            {confirmSugg && (
                <Modal testId="suggestion-confirm-modal" onClose={() => suggBusy ? null : setConfirmSugg(null)}
                       title="Confirm whitelist & approve" size="md">
                    <div className="space-y-4 text-sm">
                        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs leading-relaxed text-amber-900">
                            <div className="font-semibold mb-1 flex items-center gap-1.5">
                                <ShieldAlert className="w-3.5 h-3.5" /> This action affects future SMS auto-approval.
                            </div>
                            Adding <span className="font-mono font-semibold">{confirmSugg.suggested_sender_id}</span> to your whitelist means every future SMS from this sender ID will be trusted to auto-approve transactions. Only proceed if you recognize this as a legitimate bank sender.
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-xs">
                            <div>
                                <div className="text-slate-500 uppercase tracking-wider text-[10px] mb-0.5">Sender to whitelist</div>
                                <div className="font-mono font-semibold">{confirmSugg.suggested_sender_id}</div>
                            </div>
                            <div>
                                <div className="text-slate-500 uppercase tracking-wider text-[10px] mb-0.5">Matched UTR</div>
                                <div className="font-mono">{confirmSugg.parsed_utr || "—"}</div>
                            </div>
                            <div>
                                <div className="text-slate-500 uppercase tracking-wider text-[10px] mb-0.5">Amount (SMS)</div>
                                <div className="font-mono">{fmtINR(confirmSugg.parsed_amount)}</div>
                            </div>
                            <div>
                                <div className="text-slate-500 uppercase tracking-wider text-[10px] mb-0.5">Pending txn</div>
                                <div className="font-mono">{confirmSugg.txn?.customer_name || confirmSugg.txn?.txn_id}</div>
                            </div>
                        </div>
                        <div className="text-xs">
                            <div className="text-slate-500 uppercase tracking-wider text-[10px] mb-0.5">SMS body</div>
                            <div className="bg-slate-50 border border-slate-200 rounded p-2 font-mono text-[11px] leading-relaxed break-words">{confirmSugg.body}</div>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={() => setConfirmSugg(null)} disabled={!!suggBusy}
                                    className="btn-secondary px-3 py-1.5 rounded-md text-xs">Cancel</button>
                            <button data-testid="suggestion-confirm-accept"
                                    onClick={confirmAcceptSuggestion} disabled={!!suggBusy}
                                    className="btn-emerald px-4 py-1.5 rounded-md text-xs flex items-center gap-1.5 disabled:opacity-60">
                                {suggBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                                Yes, whitelist & approve
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
            {assignTxn && (
                <AssignUtrModal
                    txn={assignTxn}
                    scope={scope}
                    onClose={() => setAssignTxn(null)}
                    onSaved={(utr) => {
                        // Optimistically update the row in-place so the
                        // amber "Assign UTR" pill flips to the real UTR
                        // and Approve can proceed without a full refetch.
                        setItems((prev) => prev.map((it) => it.txn_id === assignTxn.txn_id ? { ...it, utr, awaiting_manual_utr: false } : it));
                        setAssignTxn(null);
                    }}
                />
            )}
            {bulkAssignOpen && (
                <BulkAssignUtrModal
                    queue={awaitingUtrRows}
                    scope={scope}
                    onClose={() => setBulkAssignOpen(false)}
                    onRowSaved={(txnId, utr) => {
                        setItems((prev) => prev.map((it) => it.txn_id === txnId ? { ...it, utr, awaiting_manual_utr: false } : it));
                    }}
                    onApproveRow={async (txnId) => {
                        // Reuse the existing act() bulk-approve plumbing so
                        // we get the same audit trail + error handling.
                        await act("approve", [txnId], { rowId: txnId });
                    }}
                />
            )}
        </div>
    );
}

